import type { ForecastResponse } from '../types'

const TODAY = Math.floor(Date.now() / 1000)
const DAY = 86400

function makeChart(count: number, startWeight = 92, slope = -0.05) {
  return Array.from({ length: count }, (_, i) => {
    const raw = startWeight + slope * i + (Math.random() - 0.5) * 0.4
    return {
      date: TODAY - (count - 1 - i) * DAY,
      weight_raw: Math.round(raw * 10) / 10,
      weight_smoothed: Math.round((startWeight + slope * i) * 10) / 10,
    }
  })
}

export type MockKey = 'no_data' | 'warmup' | 'active_high' | 'active_medium' | 'active_low' | 'active_real' | 'plateau'

export const MOCK_LABELS: Record<MockKey, string> = {
  no_data:       'NO DATA',
  warmup:        'WARMUP',
  active_high:   'ACTIVE / HIGH',
  active_medium: 'ACTIVE / MEDIUM',
  active_low:    'ACTIVE / LOW',
  active_real:   'ACTIVE / REAL DATA (narrow corridor)',
  plateau:       'PLATEAU',
}

export const MOCKS: Record<MockKey, ForecastResponse> = {
  no_data: { status: 'NO_DATA', lang: 'en' },

  warmup: {
    state: {
      status: 'WARMUP',
      current: {
        weight_raw: 91.4,
        weight_smoothed: 91.6,
        series_count: 7,
        days_since_last: 0,
        accuracy: 'HIGH',
        tempo: null,
      },
      forecast: [],
      meta: { plateau: false, next_horizon_in: 7, progress_bar: 0.5 },
    },
    chart: makeChart(7, 92, -0.05),
    lang: 'en',
  },

  active_high: {
    state: {
      status: 'ACTIVE',
      current: {
        weight_raw: 90.8,
        weight_smoothed: 91.0,
        series_count: 20,
        days_since_last: 1,
        accuracy: 'HIGH',
        tempo: -0.35,
      },
      forecast: [
        { days: 30,  value: 89.5, min: 88.2, max: 90.8 },
        { days: 90,  value: 87.0, min: 84.8, max: 89.2 },
        { days: 180, value: 84.2, min: 80.5, max: 87.9 },
      ],
      meta: { plateau: false, next_horizon_in: 10, progress_bar: 0.67 },
    },
    chart: makeChart(20, 92, -0.05),
    lang: 'en',
  },

  active_medium: {
    state: {
      status: 'ACTIVE',
      current: {
        weight_raw: 90.8,
        weight_smoothed: 91.0,
        series_count: 20,
        days_since_last: 2,
        accuracy: 'MEDIUM',
        tempo: -0.35,
      },
      forecast: [
        { days: 30,  value: 89.5, min: 88.2, max: 90.8 },
        { days: 90,  value: 87.0, min: 84.8, max: 89.2 },
        { days: 180, value: 84.2, min: 80.5, max: 87.9 },
      ],
      meta: { plateau: false, next_horizon_in: 10, progress_bar: 0.67 },
    },
    chart: makeChart(20, 92, -0.05),
    lang: 'en',
  },

  active_low: {
    state: {
      status: 'ACTIVE',
      current: {
        weight_raw: 90.8,
        weight_smoothed: 91.0,
        series_count: 30,
        days_since_last: 5,
        accuracy: 'LOW',
        tempo: -0.35,
      },
      forecast: [
        { days: 30,  value: 89.5, min: 88.2, max: 90.8 },
        { days: 90,  value: 87.0, min: 84.8, max: 89.2 },
        { days: 180, value: 84.2, min: 80.5, max: 87.9 },
        { days: 365, value: 79.1, min: 73.0, max: 85.2 },
      ],
      meta: { plateau: false, next_horizon_in: null, progress_bar: 1 },
    },
    chart: makeChart(30, 92, -0.05),
    lang: 'en',
  },

  // Реальные данные из БД: sigma=0.1885 → узкий коридор ±1 кг на 30 дней
  active_real: {
    state: {
      status: 'ACTIVE',
      current: {
        weight_raw: 80.0,
        weight_smoothed: 80.371,
        series_count: 14,
        days_since_last: 0,
        accuracy: 'HIGH',
        tempo: -1.035,  // slope * 7 = -0.1479 * 7
      },
      forecast: [
        { days: 30, value: 75.93, min: 74.90, max: 76.97 },
      ],
      meta: { plateau: false, next_horizon_in: 3, progress_bar: 0 },
    },
    chart: [
      { date: TODAY - 13 * DAY, weight_raw: 82.0, weight_smoothed: 82.000 },
      { date: TODAY - 12 * DAY, weight_raw: 83.0, weight_smoothed: 82.300 },
      { date: TODAY - 11 * DAY, weight_raw: 82.0, weight_smoothed: 82.210 },
      { date: TODAY - 10 * DAY, weight_raw: 82.0, weight_smoothed: 82.147 },
      { date: TODAY -  9 * DAY, weight_raw: 82.0, weight_smoothed: 82.103 },
      { date: TODAY -  8 * DAY, weight_raw: 81.5, weight_smoothed: 81.922 },
      { date: TODAY -  7 * DAY, weight_raw: 81.0, weight_smoothed: 81.645 },
      { date: TODAY -  6 * DAY, weight_raw: 80.9, weight_smoothed: 81.422 },
      { date: TODAY -  5 * DAY, weight_raw: 82.0, weight_smoothed: 81.595 },
      { date: TODAY -  4 * DAY, weight_raw: 80.0, weight_smoothed: 81.117 },
      { date: TODAY -  3 * DAY, weight_raw: 81.0, weight_smoothed: 81.082 },
      { date: TODAY -  2 * DAY, weight_raw: 80.0, weight_smoothed: 80.757 },
      { date: TODAY -  1 * DAY, weight_raw: 80.0, weight_smoothed: 80.530 },
      { date: TODAY,             weight_raw: 80.0, weight_smoothed: 80.371 },
    ],
    lang: 'en',
  },

  plateau: {
    state: {
      status: 'PLATEAU',
      current: {
        weight_raw: 88.3,
        weight_smoothed: 88.1,
        series_count: 25,
        days_since_last: 1,
        accuracy: 'HIGH',
        tempo: -0.042,
      },
      forecast: [],
      meta: { plateau: true, next_horizon_in: null, progress_bar: 1 },
    },
    chart: makeChart(25, 88.5, -0.002),
    lang: 'en',
  },
}
